/**
 * analytics — thin PostHog wrapper (web + product analytics).
 *
 * Cookieless by design: persistence is localStorage+memory (no cross-site
 * cookies), and only identified (logged-in) users get a person profile. SPA
 * pageviews are captured manually (see AnalyticsPageview). Everything no-ops
 * when REACT_APP_POSTHOG_KEY is unset (local dev) or during prerender/bots.
 */
import posthog from "posthog-js";

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

export const CONSENT_KEY = "ll_analytics_consent";

let enabled = false;

// Don't pollute analytics from the prerender headless browser or other bots.
const isBot = () =>
  typeof navigator !== "undefined" &&
  (navigator.webdriver || /HeadlessChrome/.test(navigator.userAgent || ""));

/** True once the visitor has explicitly granted analytics consent. */
export function hasAnalyticsConsent() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

export function initAnalytics() {
  if (enabled || !KEY || typeof window === "undefined" || isBot() || !hasAnalyticsConsent()) return;
  posthog.init(KEY, {
    api_host: HOST,
    persistence: "localStorage+memory", // cookieless
    person_profiles: "identified_only",
    autocapture: true,
    capture_pageview: false, // manual, for SPA route changes
    capture_pageleave: true,
    disable_session_recording: true,
  });
  enabled = true;
}

export const EVENTS = {
  SIGNED_UP: "signed_up",
  LOGGED_IN: "logged_in",
  LINK_CREATED: "link_created",
  UPGRADE_CLICKED: "upgrade_clicked",
};

export function capture(event, props = {}) {
  if (!enabled) return;
  posthog.capture(event, props);
}

export function capturePageview(path) {
  if (!enabled) return;
  posthog.capture("$pageview", path ? { $current_url: window.location.origin + path } : {});
}

export function identifyUser(user) {
  if (!enabled || !user?.id) return;
  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    plan: user.plan,
    role: user.role,
  });
}

export function resetUser() {
  if (!enabled) return;
  posthog.reset();
}

export default posthog;
